import {
  ELT_FORM_SECTION,
  IEltDocFormData,
  TComplexSectionData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { TUpdateELTFormData } from 'legacy/lut/components/widgets/public/partner360/ELT/models/common'
import { TMergedImages } from 'legacy/lut/components/widgets/public/partner360/ELT/models/ImageUpload'
import { TForceUpdateConfig } from 'legacy/lut/components/widgets/public/partner360/ELT/models/TForceUpdateConfig'
import { Title } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/styledComponents/title/Title'
import React, { useEffect } from 'react'

import { Flex } from 'core-components/library/flex'
import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { useEltDataSettings } from '../../../context/EltDataSettingsContext'
import { getBoTByCompanyPerformanceRevenueInitialData } from '../../../helpers/docInitialValues/getBoTByCompanyPerformanceRevenueInitialData'
import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { BalanceOfTradeTable } from '../../balanceOfTrade/balanceOfTradeTable/BalanceOfTradeTable'
import { Competitors } from '../../financialSnapshot/competitors/Competitors'
import { CustomerRevenue } from '../../revenue/customerRevenue/CustomerRevenue'
import { MultiInputWithTitle } from '../../styledComponents/multiInputWithTitle/MultiInputWithTitle'

import { Company } from './components/company/Company'
import { TopIssues } from './components/topIssues/TopIssues'
import { TopOpportunities } from './components/topOpportunities/TopOpportunities'

import styles from './DocStep1.module.scss'

interface IDocStep1Props {
  formData: IEltDocFormData
  onChangeComplexFormData: (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => void
  forceUpdate: (section: ELT_FORM_SECTION, config?: TForceUpdateConfig) => void
  fYear: number
  fQuarter: number
  netNdpValue: ActualsValueTypes
  onImageUpload: (fieldName: string, file: any, fileName: string) => void
  updateData: TUpdateELTFormData
  formIsFinalized: boolean
  displayedImages: TMergedImages
}

export const DocStep1 = ({
  formData,
  onChangeComplexFormData,
  updateData,
  onImageUpload,
  formIsFinalized,
  fQuarter,
  netNdpValue,
  fYear,
  forceUpdate,
  displayedImages,
}: IDocStep1Props) => {
  const { dataSettings } = useEltDataSettings()
  useEffect(() => {
    onChangeComplexFormData(ELT_FORM_SECTION.BALANCE_OF_TRADE)(
      getBoTByCompanyPerformanceRevenueInitialData(
        formData.CompanyPerformance,
        formData.BalanceOfTrade,
        dataSettings.ActualsValueTypes
      )
    )
  }, [formData.CompanyPerformance, onChangeComplexFormData])

  return (
    <Flex justifyContent={'space-between'} className={styles.content}>
      <Flex flexDirection={'column'} gap={'xl'} className={styles.left}>
        <Company
          formData={formData}
          onChangeComplexFormData={onChangeComplexFormData(ELT_FORM_SECTION.COMPANY_INFO)}
          updateData={updateData}
          onImageUpload={onImageUpload}
          formIsFinalized={formIsFinalized}
          displayedImages={displayedImages}
        />
        <MultiInputWithTitle
          data={formData.LastEngagement || []}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.LAST_ENGAGEMENT)}
          titleText={getFormedEltTranslation('section-doc-past-elt-engagement')}
          withDot
        />
        <MultiInputWithTitle
          data={formData.MeetingObjectives || []}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.MEETING_OBJECTIVES)}
          titleText={getFormedEltTranslation('section-doc-meeting-objectives')}
          withDot
        />
        <MultiInputWithTitle
          data={formData.HpExecutiveKeyPoints || []}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.HP_EXECUTIVE_KEY_POINTS)}
          titleText={getFormedEltTranslation('section-doc-hp-executive-points')}
          withDot
        />
        <TopIssues
          data={formData.TopIssues}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.TOP_ISSUES)}
        />
        <MultiInputWithTitle
          data={formData.AdditionalNotes || []}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.ADDITIONAL_NOTES)}
          titleText={getFormedEltTranslation('section-doc-additional-notes')}
          withDot
        />
      </Flex>

      <Flex flexDirection={'column'} gap={'xl'} className={styles.right}>
        <CustomerRevenue
          fYear={fYear}
          fQuarter={fQuarter}
          data={formData.CustomerRevenue}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.CUSTOMER_REVENUE)}
          netNdpValue={netNdpValue}
        />

        <Competitors
          isDocVersion
          fYear={fYear}
          fQuarter={fQuarter}
          forceUpdate={forceUpdate}
          data={formData.CompetitiveLandscape || []}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.COMPETITORS)}
        />
        <TopOpportunities
          data={formData.TopOpportunities || []}
          updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.TOP_OPPORTUNITIES)}
        />
        <div>
          <Title
            variant="withBackground"
            text={getFormedEltTranslation('doc-section-balance-of-trade')}
          />
          <BalanceOfTradeTable
            fYear={fYear}
            balanceOfTradeData={formData.BalanceOfTrade}
            updateFormData={onChangeComplexFormData(ELT_FORM_SECTION.BALANCE_OF_TRADE)}
            netNdpValue={netNdpValue}
          />
        </div>
      </Flex>
    </Flex>
  )
}
